import React from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import Link from 'components/Link'
import SignInForm from 'components/SignInForm'
import { Text } from 'components/Box'
import Form from 'components/Form'
import ApolloErrorComponent from 'components/ApolloError'
import { routes } from 'constants/routes'

import RedirectBadge from 'components/RedirectBadge'
import {
  Panel as StyledPanel,
  WordBreak,
  InvitedEmail,
  BodyPanel,
  PanelFooter,
  Content,
} from './shared'
import { Inviter } from './types'
import { ParsedError } from 'graphql/apollo/useMutation'
import { Title } from 'components/IndexLayout'
import queryString from 'query-string'
import useUserProfile from 'modules/user/useUserProfile'

interface SignInAndAcceptMembershipPanelProps {
  invitedEmail?: string
  inviter: Inviter
  documentName: string
  inviteToken: string
  acceptShareMembership(): void
  acceptShareMembershipError?: ParsedError
  acceptShareMembershipLoading: boolean
}

const SignInAndAcceptMembershipPanel: React.FC<SignInAndAcceptMembershipPanelProps> = ({
  invitedEmail,
  inviter,
  documentName,
  inviteToken,
  acceptShareMembership,
  acceptShareMembershipError,
  acceptShareMembershipLoading,
}) => {
  const location = useLocation()
  const history = useHistory()
  const query = queryString.parse(location.search)
  const { data: profile } = useUserProfile()

  const userEmail = profile?.me.email

  return (
    <StyledPanel>
      <RedirectBadge
        from={location.pathname}
        documentInvitation={{ inviter, documentName }}
      />

      <SignInForm
        email={invitedEmail}
        onCompleted={() => {
          if (invitedEmail === userEmail) {
            acceptShareMembership()
          } else {
            history.push(
              routes.SHARE_ACCEPT_INVITE.create({
                query,
              }),
              {
                from: location.pathname,
              }
            )
          }
        }}>
        {formProps => (
          <>
            <BodyPanel>
              <Title>Sign in to Sketch</Title>
              <Content mb={32}>
                <WordBreak>
                  To access the document now, sign in to{' '}
                  <InvitedEmail>
                    <strong>{invitedEmail}</strong>
                  </InvitedEmail>
                  . Alternatively, sign in or{' '}
                  <Link
                    to={{
                      pathname: routes.SHARE_ACCEPT_INVITE_SIGN_UP.create({}),
                      search: `?token=${inviteToken}`,
                    }}
                    variant="secondary"
                    isUnderlined>
                    sign up
                  </Link>{' '}
                  with a different email and request access.
                </WordBreak>
              </Content>

              <SignInForm.Fields
                {...formProps}
                forgotPasswordLink={{
                  pathname: routes.SHARE_ACCEPT_INVITE_FORGOT_PASSWORD.create(
                    {}
                  ),
                  search: `?token=${inviteToken}`,
                }}
              />
            </BodyPanel>
            <PanelFooter>
              {acceptShareMembershipError && (
                <ApolloErrorComponent
                  style={{ textAlign: 'left' }}
                  my={4}
                  error={acceptShareMembershipError}
                />
              )}
              {formProps.status && (
                <Form.Error>
                  <Text textAlign="center" mt={0}>
                    {formProps.status}
                  </Text>
                </Form.Error>
              )}
              {formProps?.apiErrors?.messages?.map(
                (message, index) =>
                  message && (
                    <Form.Error key={index}>
                      <Text textAlign="center" mt={0}>
                        {message}
                      </Text>
                    </Form.Error>
                  )
              )}
              <SignInForm.Submit
                isSubmitting={
                  formProps.isSubmitting || acceptShareMembershipLoading
                }
              />
            </PanelFooter>
          </>
        )}
      </SignInForm>
    </StyledPanel>
  )
}

export default SignInAndAcceptMembershipPanel
